"use client";

import { useState } from "react";
import Link from "next/link";
import { Brain, ChevronLeft, Volume2, Youtube, RefreshCw, RotateCcw, ChevronRight, Lightbulb, Image as ImageIcon, Sparkles } from "lucide-react";
import { ThemeToggle } from "@/lib/theme";
import { SAMPLE_WORDS } from "@/lib/vocab/sampleData";

const STUDY_WORDS = SAMPLE_WORDS.filter(w => w.status !== "mastered");

export default function StudyPage() {
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [sessionResults, setSessionResults] = useState<{ word: string; rating: string }[]>([]);
  const [done, setDone] = useState(false);

  const word = STUDY_WORDS[cardIndex];

  function rate(rating: "again" | "hard" | "good" | "easy") {
    setSessionResults(r => [...r, { word: word.word, rating }]);
    if (cardIndex + 1 >= STUDY_WORDS.length) {
      setDone(true);
    } else {
      setCardIndex(i => i + 1);
      setFlipped(false);
    }
  }

  if (done) {
    const counts = { again: 0, hard: 0, good: 0, easy: 0 };
    sessionResults.forEach(r => counts[r.rating as keyof typeof counts]++);
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-primary)" }}>
        <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
          <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between">
            <Link href="/vocab" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <Brain size={16} className="text-blue-500" /> VocabMaster
            </Link>
            <ThemeToggle />
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="mb-4"><Sparkles className="w-6 h-6 mx-auto text-amber-400" strokeWidth={1.75} aria-hidden="true" /></div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>Session Complete!</h2>
            <p className="mb-6" style={{ color: "var(--text-muted)" }}>You studied {STUDY_WORDS.length} cards</p>
            <div className="grid grid-cols-4 gap-2 mb-6">
              {[
                { label: "Again", count: counts.again, color: "text-red-500 bg-red-50" },
                { label: "Hard", count: counts.hard, color: "text-yellow-600 bg-yellow-50" },
                { label: "Good", count: counts.good, color: "text-green-600 bg-green-50" },
                { label: "Easy", count: counts.easy, color: "text-blue-600 bg-blue-50" },
              ].map(({ label, count, color }) => (
                <div key={label} className={`rounded-xl p-3 text-center ${color}`}>
                  <div className="text-xl font-bold">{count}</div>
                  <div className="text-xs mt-0.5">{label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={() => { setCardIndex(0); setFlipped(false); setDone(false); setSessionResults([]); }}
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                <RotateCcw size={16} /> Study Again
              </button>
              <Link href="/vocab/quiz"
                className="w-full py-3 rounded-xl font-medium text-sm text-center hover:opacity-80 transition-opacity"
                style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
                Take Mastery Quiz
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-primary)" }}>
      {/* Nav */}
      <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/vocab/pool" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            <ChevronLeft size={16} /> Pool
          </Link>
          <div className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>
            Card {cardIndex + 1} of {STUDY_WORDS.length}
          </div>
          <ThemeToggle />
        </div>
      </nav>

      {/* Progress */}
      <div className="h-1" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="h-1 bg-blue-500 transition-all duration-300"
          style={{ width: `${((cardIndex) / STUDY_WORDS.length) * 100}%` }} />
      </div>

      <div className="flex-1 flex flex-col items-center px-4 py-6 max-w-xl mx-auto w-full">

        {/* Card */}
        <div
          onClick={() => setFlipped(f => !f)}
          className="w-full rounded-2xl border cursor-pointer select-none transition-all duration-200 hover:shadow-lg mb-4"
          style={{ backgroundColor: "var(--bg-card)", borderColor: flipped ? "#3b82f6" : "var(--border)", minHeight: "420px" }}
        >
          {!flipped ? (
            /* FRONT */
            <div className="flex flex-col items-center justify-center h-full p-8 text-center" style={{ minHeight: "420px" }}>
              <p className="text-xs uppercase tracking-widest mb-6" style={{ color: "var(--text-faint)" }}>
                {word.partOfSpeech}
              </p>
              <h1 className="text-5xl font-bold mb-3 tracking-tight" style={{ color: "var(--text-primary)" }}>
                {word.word}
              </h1>
              <p className="text-lg mb-6" style={{ color: "var(--text-muted)" }}>{word.pronunciation}</p>
              <button className="flex items-center gap-2 px-4 py-2 rounded-full text-sm border hover:opacity-70 transition-opacity"
                style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
                onClick={e => e.stopPropagation()}>
                <Volume2 size={15} /> Play Audio
              </button>
              <p className="mt-8 text-xs" style={{ color: "var(--text-faint)" }}>Tap to reveal</p>
            </div>
          ) : (
            /* BACK */
            <div className="p-6" style={{ minHeight: "420px" }}>
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>{word.word}</h2>
                  <p style={{ color: "var(--text-muted)" }}>{word.partOfSpeech} · {word.pronunciation}</p>
                </div>
                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs hover:opacity-70"
                  style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}
                  onClick={e => e.stopPropagation()}>
                  <Volume2 size={12} /> Audio
                </button>
              </div>

              {/* Definition */}
              <p className="text-sm mb-4 font-medium" style={{ color: "var(--text-secondary)" }}>{word.definition}</p>

              {/* Mnemonic */}
              <div className="rounded-xl p-3 mb-4 flex gap-2" style={{ backgroundColor: "#fefce8", border: "1px solid #fef08a" }}>
                <Lightbulb size={16} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-yellow-700 mb-0.5">AI Mnemonic</p>
                  <p className="text-sm italic text-yellow-900">{word.mnemonic}</p>
                </div>
              </div>

              {/* Image placeholder */}
              <div className="rounded-xl mb-4 flex items-center justify-center"
                style={{ backgroundColor: "var(--bg-secondary)", height: "100px", border: "1px dashed var(--border)" }}>
                <div className="text-center">
                  <ImageIcon size={24} style={{ color: "var(--text-faint)" }} className="mx-auto mb-1" />
                  <p className="text-xs" style={{ color: "var(--text-faint)" }}>AI Image (generated on demand)</p>
                </div>
              </div>

              {/* Examples */}
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>Examples</p>
                {word.examples.map((ex, i) => (
                  <p key={i} className="text-sm mb-2 pl-3 border-l-2 border-blue-300"
                    style={{ color: "var(--text-secondary)" }}
                    dangerouslySetInnerHTML={{ __html: ex.replace(new RegExp(word.word, "gi"), `<strong style="color:var(--blue-text)">$&</strong>`) }}
                  />
                ))}
              </div>

              {/* YouTube */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                  <Youtube size={12} className="inline mr-1" />YouTube Context
                </p>
                <div className="space-y-2">
                  {word.youtubeLinks.map((yt, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg p-2.5 hover:opacity-80 transition-opacity cursor-pointer"
                      style={{ backgroundColor: "var(--bg-secondary)" }}>
                      <div className="w-12 h-9 rounded flex items-center justify-center flex-shrink-0 bg-red-600">
                        <Youtube size={14} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: "var(--text-primary)" }}>{yt.title}</p>
                        <p className="text-xs" style={{ color: "var(--text-muted)" }}>{yt.channel} · {yt.duration}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Rating buttons */}
        {flipped && (
          <div className="w-full">
            <p className="text-xs text-center mb-2" style={{ color: "var(--text-muted)" }}>How well did you know this?</p>
            <div className="grid grid-cols-4 gap-2">
              {[
                { key: "again" as const, label: "Again", sub: "Forgot", cls: "border-red-200 bg-red-50 text-red-600 hover:bg-red-100" },
                { key: "hard" as const, label: "Hard", sub: "Struggled", cls: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100" },
                { key: "good" as const, label: "Good", sub: "Got it", cls: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100" },
                { key: "easy" as const, label: "Easy", sub: "Perfect", cls: "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100" },
              ].map(({ key, label, sub, cls }) => (
                <button key={key} onClick={() => rate(key)}
                  className={`py-3 rounded-xl border-2 font-semibold text-sm transition-colors ${cls}`}>
                  {label}
                  <div className="text-xs font-normal opacity-60">{sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Nav arrows (when not flipped) */}
        {!flipped && (
          <div className="flex items-center gap-6">
            <button onClick={() => { if (cardIndex > 0) { setCardIndex(i => i - 1); setFlipped(false); } }}
              className="p-2 rounded-full hover:opacity-70 transition-opacity"
              style={{ color: "var(--text-muted)" }}>
              <ChevronLeft size={20} />
            </button>
            <button onClick={() => setFlipped(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2">
              Reveal <RefreshCw size={14} />
            </button>
            <button onClick={() => { setCardIndex(i => i + 1); setFlipped(false); }}
              className="p-2 rounded-full hover:opacity-70 transition-opacity"
              style={{ color: "var(--text-muted)" }}>
              <ChevronRight size={20} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
